import { FC } from "react";
import { ConfirmationStep } from "../../components/confirmationStep/ConfirmationStep";
import InfoBox from "../../components/infoBox/InfoBox";
import { InsertCodeStep } from "../../components/insertCodeStep/InsertCodeStep";
import { SendEmailStep } from "../../components/sendEmailStep/SendEmailStep";
import { Layout } from "../../containers/layout";
import { SubmitEmailDto } from "../../models/signupEmailDto";
import { SignupUserDto } from "../../models/signupUserDto";
import { CompleteRegistrationStep } from "../../components/completeUserRegistrationStep/CompleteRegistrationStep";
import { useWorkflowIdWithNotification } from "../../hooks/useGetWorkflowId/useWorkflowIdWithNotification";
import {
  useNewUserFlow,
  NewUserStepEnum,
  NewUserFlowProvider
} from "../../contexts/newUserContext";
import { TranslationKeyType } from "../../hooks/useTranslationHook/types";
import { useSubmitCode } from "../../hooks/useSubmitCode/useSubmitCode";
import { useSubmitEmail } from "../../hooks/useSubmitEmail/useSubmitEmail";
import { useSubmitUser } from "../../hooks/useSubmitUser/useSubmitUser";
import { useToastNotification } from "../../hooks/useToastNotification/useToastNotification";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { navigateSecurely } from "../../utils/navigation/navigationUtils";

export const NewUserPage: FC = () => {
  const {
    currentStep,
    setCurrentStep,
    setEmail,
    email,
    workflowId,
    resetFlow,
    setWorkflowId,
    setLanguage,
    language
  } = useNewUserFlow();
  const { getWorkflowIdSafely } = useWorkflowIdWithNotification();
  const { submitUser, loading: userLoading } = useSubmitUser();
  const { submitCode, loading: codeLoading } = useSubmitCode();
  const { submitEmail, loading: resendLoading } = useSubmitEmail();
  const { showNotification, NotificationElement } = useToastNotification();

  const { translateString } = useTranslation();

  const handleEmailSubmit = async (dto: SubmitEmailDto) => {
    try {
      await submitEmail(dto);

      setEmail(dto.email);
      setWorkflowId(dto.workflowId);
      setLanguage(dto.language);
      setCurrentStep(NewUserStepEnum.InsertCode);
    } catch (err: any) {
      showNotification(translateString("error_sending_email"), "error");
    }
  };

  const handleCodeSubmit = async (code: string) => {
    if (!workflowId) {
      showNotification(translateString("workflow_missing_error"), "error");
      return;
    }

    try {
      await submitCode(code, workflowId);
      setCurrentStep(NewUserStepEnum.CompleteRegistration);
    } catch (err: any) {
      const responseBody = err.response?.data || err;

      const errors = Array.isArray(responseBody?.errors)
        ? (responseBody.errors as BackendErrorDetail[])
        : [];

      if (errors.length > 0) {
        errors.forEach((errorDetail) => {
          const key = errorDetail.code as TranslationKeyType;
          const msg = translateString(key) ?? errorDetail.message;
          showNotification(msg, "error");
        });
      } else {
        showNotification(translateString("error_verifying_code"), "error");
      }
    }
  };

  const handleResendEmail = async () => {
    if (!email) {
      showNotification(translateString("workflow_missing_error"), "error");
      return;
    }

    const newWorkflowId = await getWorkflowIdSafely();
    if (!newWorkflowId) return;

    setWorkflowId(newWorkflowId);

    const signupDto: SubmitEmailDto = {
      email,
      workflowId: newWorkflowId,
      language: language ?? ""
    };

    await submitEmail(signupDto);
    showNotification(translateString("verification_email_info"), "info");
  };

  const handleUserSubmit = async (userDto: SignupUserDto) => {
    try {
      await submitUser(userDto);
      setCurrentStep(NewUserStepEnum.RegistrationComplete);
    } catch (err: unknown) {
      const errors = Array.isArray(err) ? (err as BackendErrorDetail[]) : [];

      errors.forEach((err) => {
        const key = err.code as TranslationKeyType;
        const msg = translateString(key) ?? err.message;
        showNotification(msg, "error");
      });
    }
  };

  const handleCancel = async () => {
    resetFlow();
  };

  const handleReturnToLogin = () => {
    navigateSecurely();
  };

  const renderStep = () => {
    switch (currentStep) {
      case NewUserStepEnum.SendEmail:
        return (
          <SendEmailStep
            title={translateString("new_user")}
            description={translateString("new_user_description")}
            infoBox={<InfoBox type="primary">{translateString("new_user_information")}</InfoBox>}
            onSubmit={handleEmailSubmit}
            loading={resendLoading}
          />
        );
      case NewUserStepEnum.InsertCode:
        return (
          <InsertCodeStep
            onSubmit={handleCodeSubmit}
            onResend={handleResendEmail}
            onCancel={handleCancel}
            loading={codeLoading}
            resendLoading={resendLoading}
          />
        );
      case NewUserStepEnum.CompleteRegistration:
        return (
          <CompleteRegistrationStep
            email={email ?? ""}
            workflowId={workflowId ?? ""}
            languageOverride={language}
            onSubmit={handleUserSubmit}
            onCancel={handleCancel}
            loading={userLoading}
          />
        );
      case NewUserStepEnum.RegistrationComplete:
        return (
          <ConfirmationStep
            cardTitle={translateString("registration_complete")}
            message={translateString("registration_complete_description")}
            buttonText={translateString("return_to_login")}
            onButtonClick={handleReturnToLogin}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      {renderStep()}
      {NotificationElement}
    </Layout>
  );
};

export const NewUserPageWithProvider: FC = () => {
  return (
    <NewUserFlowProvider>
      <NewUserPage />
    </NewUserFlowProvider>
  );
};
